import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import Button from './Button';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showCloseButton?: boolean;
  closable?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  closable = true,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closable) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, closable]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl'
  };

  const modalContent = (
    <div className="fixed inset-0 z-modal overflow-y-auto animate-fade-in">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Enhanced Backdrop */}
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-md transition-opacity animate-fade-in"
          onClick={closable ? onClose : undefined}
        />
        
        {/* Modal */}
        <div className={`relative w-full ${sizeClasses[size]} transform transition-all duration-normal ease-smooth animate-scale-in`}>
          <div className="glass-card rounded-3xl p-8 shadow-glass-strong border border-white/20">
            {/* Header */}
            {(title || showCloseButton) && (
              <div className="flex items-center justify-between mb-6">
                {title && (
                  <h3 className="text-xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>
                    {title}
                  </h3>
                )}
                
                {showCloseButton && closable && (
                  <button
                    onClick={onClose}
                    className="p-2 glass-button rounded-full transition-fast ease-smooth hover:scale-110 active:scale-95"
                    style={{color: 'var(--liquid-text-primary)'}}
                    aria-label="Close"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>
            )}
            
            {/* Content */}
            <div style={{color: 'var(--liquid-text-primary)'}}>{children}</div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default Modal;
